import { z } from 'zod';
import { uuidSchema } from './common.schema';

// ---------------------------------------------------------------------------
// Enum constants
// ---------------------------------------------------------------------------

export const PRODUCT_REQUEST_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'] as const;
export type ProductRequestStatus = (typeof PRODUCT_REQUEST_STATUSES)[number];

export const REJECTION_REASONS = [
  'DUPLICATE',
  'INSUFFICIENT_DATA',
  'NOT_APPLICABLE',
  'REGULATORY_BLOCK',
  'OTHER',
] as const;
export type RejectionReason = (typeof REJECTION_REASONS)[number];

export const PRODUCT_REQUEST_NOTIFICATION_STATUSES = ['PENDING', 'SENT', 'FAILED'] as const;
export type ProductRequestNotificationStatus =
  (typeof PRODUCT_REQUEST_NOTIFICATION_STATUSES)[number];

// ---------------------------------------------------------------------------
// POST /product-requests — tenant submits a new request
// ---------------------------------------------------------------------------

export const SubmitProductRequestSchema = z.object({
  // -- Identity context (passed by inventory service from the end-user JWT) ---
  /** UUID of the active catalog copy already written to TMENS_Products */
  hospital_catalog_product_id: uuidSchema,
  tenant_id: z.string().trim().min(1),
  hospital_id: z.string().trim().min(1),
  hospital_name: z.string().trim().min(1).max(255),
  /** Tenant hostname (e.g. tenant1.timelesslabs.com) used to resolve the correct notification service URL */
  tenant_hostname: z.string().trim().min(1),
  requested_by: z.string().trim().min(1),          // Cognito sub UUID
  requested_by_name: z.string().trim().min(1).max(255),
  requested_by_email: z.string().trim().email().max(255),
  requested_by_role: z.string().trim().min(1).max(64),

  // -- Request fields ---------------------------------------------------------
  request_title: z.string().trim().min(1).max(300),
  suggested_product_name: z.string().trim().min(1).max(255),
  suggested_brand: z.string().trim().max(255).optional(),
  suggested_manufacturer: z.string().trim().max(255).optional(),
  suggested_sku: z.string().trim().max(64).optional(),
  suggested_category: z.string().trim().max(128).optional(),
  request_reason: z.string().trim().max(500).default('Product added to local catalogue'),
  /**
   * Partial map mirroring TMENS_Products attributes the tenant can supply.
   * All fields optional — the admin fills in missing data during review.
   */
  product_data: z.record(z.string(), z.unknown()).optional(),
});

export type SubmitProductRequest = z.infer<typeof SubmitProductRequestSchema>;

// ---------------------------------------------------------------------------
// POST /product-requests/:requestId/approve — TMS admin approves
// ---------------------------------------------------------------------------

export const ApproveProductRequestSchema = z.object({
  approved_product_name: z.string().trim().min(1).max(255),
  review_notes: z.string().trim().min(1).max(1000),
  internal_notes: z.string().trim().max(1000).optional(),
  /**
   * Admin-enriched product data merged over the tenant-submitted product_data.
   * Must contain enough fields for a valid TMENS_Products write.
   */
  product_data: z.record(z.string(), z.unknown()).optional(),
});

export type ApproveProductRequest = z.infer<typeof ApproveProductRequestSchema>;

// ---------------------------------------------------------------------------
// POST /product-requests/:requestId/reject — TMS admin rejects
// ---------------------------------------------------------------------------

export const RejectProductRequestSchema = z.object({
  rejection_reason: z.enum(REJECTION_REASONS),
  review_notes: z.string().trim().min(1).max(1000),
  rejection_detail: z.string().trim().max(1000).optional(),
  internal_notes: z.string().trim().max(1000).optional(),
});

export type RejectProductRequest = z.infer<typeof RejectProductRequestSchema>;

// ---------------------------------------------------------------------------
// POST /product-requests/:requestId/action — single endpoint for approve or reject
// ---------------------------------------------------------------------------

/**
 * Flat schema so Fastify / zodToJsonSchema renders a single, clean JSON Schema
 * object instead of a discriminated anyOf that can validate both branches at
 * once.  Field-level requirements are enforced by superRefine:
 *   approve → approved_product_name required
 *   reject  → rejection_reason required
 */
export const ActionProductRequestSchema = z
  .object({
    action: z.enum(['approve', 'reject'] as const),
    /** UUID of the admin taking this action */
    action_by: uuidSchema,
    // ── approve fields ──────────────────────────────────────────────────────
    approved_product_name: z.string().trim().min(1).max(255).optional(),
    // ── shared ──────────────────────────────────────────────────────────────
    review_notes: z.string().trim().min(1).max(1000),
    internal_notes: z.string().trim().max(1000).optional(),
    // ── reject fields ───────────────────────────────────────────────────────
    rejection_reason: z.enum(REJECTION_REASONS).optional(),
    rejection_detail: z.string().trim().max(1000).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.action === 'approve' && !data.approved_product_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['approved_product_name'],
        message: 'approved_product_name is required when action is "approve"',
      });
    }
    if (data.action === 'reject' && !data.rejection_reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rejection_reason'],
        message: 'rejection_reason is required when action is "reject"',
      });
    }
  });

export type ActionProductRequest = z.infer<typeof ActionProductRequestSchema>;

// ---------------------------------------------------------------------------
// GET list query — shared pagination + optional filters
// ---------------------------------------------------------------------------

export const ProductRequestListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  /** Base-64 encoded LastEvaluatedKey from the previous page */
  cursor: z.string().optional(),
});

export type ProductRequestListQuery = z.infer<typeof ProductRequestListQuerySchema>;

// ---------------------------------------------------------------------------
// GET /admin/history query — filter by resolved status
// ---------------------------------------------------------------------------

export const AdminProductRequestHistoryQuerySchema = ProductRequestListQuerySchema.extend({
  status: z
    .preprocess(
      (val) => (val === undefined ? undefined : Array.isArray(val) ? val : [val]),
      z.array(z.enum(['APPROVED', 'REJECTED'])).min(1).optional(),
    )
    .optional(),
});

export type AdminProductRequestHistoryQuery = z.infer<
  typeof AdminProductRequestHistoryQuerySchema
>;

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

/**
 * Full item — returned on GetItem, approve, and reject.
 * `internal_notes` is present in this schema; the service layer strips it for
 * non-admin callers before returning a response.
 */
export const ProductRequestDynamoItemSchema = z.object({
  // -- Identity ---------------------------------------------------------------
  request_id: uuidSchema,
  request_reference: z.string(),
  item_type: z.literal('PRODUCT_REQUEST'),

  // -- Requester / Tenant context ---------------------------------------------
  tenant_id: z.string(),
  hospital_id: z.string(),
  hospital_name: z.string(),
  /** Stored at creation time — used by the control-plane products service to resolve the correct tenant notification URL on approve/reject */
  tenant_hostname: z.string(),
  /** UUID of the hospital catalog copy that triggered this request (audit link) */
  hospital_catalog_product_id: uuidSchema,
  requested_by: z.string(),
  requested_by_name: z.string(),
  requested_by_email: z.string(),
  requested_by_role: z.string(),
  requested_at: z.string(),

  // -- Request metadata -------------------------------------------------------
  request_title: z.string(),
  request_reason: z.string(),

  // -- Suggested product identification ---------------------------------------
  suggested_product_name: z.string(),
  suggested_brand: z.string().nullable().optional(),
  suggested_manufacturer: z.string().nullable().optional(),
  suggested_category: z.string().nullable().optional(),
  suggested_sku: z.string().nullable().optional(),

  // -- Product data snapshot --------------------------------------------------
  product_data: z.record(z.string(), z.unknown()).nullable().optional(),

  // -- Status & workflow ------------------------------------------------------
  status: z.enum(PRODUCT_REQUEST_STATUSES),

  // -- TMS admin review -------------------------------------------------------
  reviewed_by: z.string().nullable().optional(),
  reviewed_by_name: z.string().nullable().optional(),
  reviewed_at: z.string().nullable().optional(),
  review_notes: z.string().nullable().optional(),
  /** TMS team-only. Never returned to tenant role callers. */
  internal_notes: z.string().nullable().optional(),
  rejection_reason: z.enum(REJECTION_REASONS).nullable().optional(),
  rejection_detail: z.string().nullable().optional(),

  // -- Approval outcome -------------------------------------------------------
  approved_product_id: uuidSchema.nullable().optional(),
  approved_product_name: z.string().nullable().optional(),

  /** Rendered notification subject stored at decision time (from PG notification_templates). */
  notification_subject: z.string().nullable().optional(),
  /** Rendered notification body stored at decision time (from PG notification_templates). */
  notification_body: z.string().nullable().optional(),
  /** Priority of the notification (mirrors notification_templates.priority). */
  notification_priority: z.string().nullable().optional(),
  /** UUIDs of the Cognito users this notification was dispatched to. */
  notified_user_ids: z.array(z.string()).nullable().optional(),

  // -- Tenant notification tracking -------------------------------------------
  tenant_notification_status: z.enum(PRODUCT_REQUEST_NOTIFICATION_STATUSES),
  tenant_notified_at: z.string().nullable().optional(),
  tenant_notification_error: z.string().nullable().optional(),
  /** Set when the requesting user acknowledges the approve/reject decision on login. */
  tenant_notification_seen_at: z.string().nullable().optional(),

  // -- Timestamps -------------------------------------------------------------
  created_at: z.string(),
  updated_at: z.string(),
});

export type ProductRequestDynamoItem = z.infer<typeof ProductRequestDynamoItemSchema>;

/**
 * Projected subset — used in list views (admin inbox, tenant history).
 * Does not include `product_data`, `internal_notes`, or raw GSI keys.
 */
export const ProductRequestListItemSchema = z.object({
  request_id: uuidSchema,
  request_reference: z.string(),
  status: z.enum(PRODUCT_REQUEST_STATUSES),
  tenant_id: z.string(),
  hospital_id: z.string(),
  hospital_name: z.string(),
  requested_by_name: z.string(),
  requested_at: z.string(),
  suggested_product_name: z.string(),
  suggested_brand: z.string().nullable().optional(),
  review_notes: z.string().nullable().optional(),
  rejection_reason: z.enum(REJECTION_REASONS).nullable().optional(),
  rejection_detail: z.string().nullable().optional(),
  approved_product_id: uuidSchema.nullable().optional(),
  approved_product_name: z.string().nullable().optional(),
  tenant_notification_status: z.enum(PRODUCT_REQUEST_NOTIFICATION_STATUSES),
  /** Rendered notification subject stored at creation time. */
  notification_subject: z.string().nullable().optional(),
  /** Rendered notification body stored at creation time. */
  notification_body: z.string().nullable().optional(),
  /** Priority of the notification. */
  notification_priority: z.string().nullable().optional(),
  /** Null = user has not yet seen the approve/reject decision. */
  tenant_notification_seen_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type ProductRequestListItem = z.infer<typeof ProductRequestListItemSchema>;

/**
 * Paginated list response.
 */
export const ProductRequestListResponseSchema = z.object({
  items: z.array(ProductRequestListItemSchema),
  /** Present when more pages exist; pass as `cursor` query param */
  next_cursor: z.string().nullable(),
  count: z.number().int(),
});

export type ProductRequestListResponse = z.infer<typeof ProductRequestListResponseSchema>;

/**
 * Pending count response — drives the admin inbox badge.
 */
export const ProductRequestPendingCountSchema = z.object({
  count: z.number().int(),
});

export type ProductRequestPendingCount = z.infer<typeof ProductRequestPendingCountSchema>;
